import { type V3ActivityStatus } from '@apps/web/features/phoebe_chat_v3/model/activity_presentation';
import { type V3ReasoningMilestoneStep } from '@apps/web/features/phoebe_chat_v3/model/transcript';
import {
    isDisplayOnlyV3WorkingStatus,
    isFallbackV3WorkingStatus,
} from '@apps/web/features/phoebe_chat_v3/model/working_status';

export type V3WorkingTraceStep = {
    readonly id: string;
    readonly source: 'milestone' | 'status';
    readonly label: string;
    readonly detail: string | null;
};

export type V3WorkingTraceState = {
    readonly status: V3ActivityStatus;
    readonly steps: ReadonlyArray<V3WorkingTraceStep>;
    readonly nextTransientStepNumber: number;
};

function statusesMatch(
    first: V3ActivityStatus,
    second: V3ActivityStatus,
): boolean {
    return (
        first.kind === second.kind &&
        first.label === second.label &&
        first.detail === second.detail
    );
}

function milestoneTraceStep(
    milestone: V3ReasoningMilestoneStep,
): V3WorkingTraceStep {
    return {
        id: milestone.id,
        source: 'milestone',
        label: milestone.label,
        detail: milestone.detail,
    };
}

// A status rebuilt from a milestone keeps its label but loses its detail.
// Treat the missing detail as a wildcard so the active row does not repeat
// the same milestone as a completed row.
export function statusMatchesV3WorkingTraceStep(
    status: V3ActivityStatus,
    step: Pick<V3WorkingTraceStep, 'label' | 'detail'>,
): boolean {
    return (
        status.label === step.label &&
        (status.detail === null || status.detail === step.detail)
    );
}

export function createV3WorkingTrace(
    status: V3ActivityStatus,
    completedSteps: ReadonlyArray<V3ReasoningMilestoneStep>,
): V3WorkingTraceState {
    return {
        status,
        steps: completedSteps.map(milestoneTraceStep),
        nextTransientStepNumber: 0,
    };
}

export function retainLastV3WorkingStatus(
    trace: V3WorkingTraceState,
    status: V3ActivityStatus,
): V3ActivityStatus {
    return isFallbackV3WorkingStatus(status) ? trace.status : status;
}

export function advanceV3WorkingTrace(
    trace: V3WorkingTraceState,
    {
        status,
        completedSteps,
    }: {
        readonly status: V3ActivityStatus;
        readonly completedSteps: ReadonlyArray<V3ReasoningMilestoneStep>;
    },
): V3WorkingTraceState {
    const steps = [...trace.steps];
    const previousStatusChanged = !statusesMatch(trace.status, status);
    let changed = previousStatusChanged;
    let nextTransientStepNumber = trace.nextTransientStepNumber;
    const stepIds = new Set(steps.map((step) => step.id));
    const matchingMilestone = completedSteps.find(
        (milestone) =>
            !stepIds.has(milestone.id) &&
            statusMatchesV3WorkingTraceStep(trace.status, milestone),
    );

    if (previousStatusChanged && matchingMilestone) {
        steps.push(milestoneTraceStep(matchingMilestone));
        stepIds.add(matchingMilestone.id);
        changed = true;
    } else if (
        previousStatusChanged &&
        !matchingMilestone &&
        !isDisplayOnlyV3WorkingStatus(trace.status) &&
        !statusMatchesV3WorkingTraceStep(
            trace.status,
            steps[steps.length - 1] ?? { label: '', detail: null },
        )
    ) {
        nextTransientStepNumber += 1;
        steps.push({
            id: `working-status-${nextTransientStepNumber}`,
            source: 'status',
            label: trace.status.label,
            detail: trace.status.detail,
        });
        changed = true;
    }

    const exactTransientIndexes = new Map<
        string,
        { indexes: Array<number>; next: number }
    >();
    const wildcardTransientIndexes = new Map<
        string,
        { indexes: Array<number>; next: number }
    >();
    for (const [index, step] of steps.entries()) {
        if (step.source !== 'status') {
            continue;
        }
        const indexMap =
            step.detail === null
                ? wildcardTransientIndexes
                : exactTransientIndexes;
        const key =
            step.detail === null
                ? step.label
                : JSON.stringify([step.label, step.detail]);
        const queue = indexMap.get(key);
        if (queue) {
            queue.indexes.push(index);
        } else {
            indexMap.set(key, { indexes: [index], next: 0 });
        }
    }

    function takeTransientIndex(
        milestone: V3ReasoningMilestoneStep,
    ): number | null {
        function takeIndex(
            queue: { indexes: Array<number>; next: number } | undefined,
        ): number | null {
            if (!queue || queue.next >= queue.indexes.length) {
                return null;
            }
            const index = queue.indexes[queue.next];
            queue.next += 1;
            return index ?? null;
        }

        const exactQueue =
            milestone.detail === null
                ? undefined
                : exactTransientIndexes.get(
                      JSON.stringify([milestone.label, milestone.detail]),
                  );
        return (
            takeIndex(exactQueue) ??
            takeIndex(wildcardTransientIndexes.get(milestone.label))
        );
    }

    // A live status can arrive before its saved milestone. Replace that
    // temporary step when the saved transcript catches up.
    for (const milestone of completedSteps) {
        if (stepIds.has(milestone.id)) {
            continue;
        }
        const transientIndex = takeTransientIndex(milestone);
        if (transientIndex !== null) {
            steps[transientIndex] = milestoneTraceStep(milestone);
        } else {
            steps.push(milestoneTraceStep(milestone));
        }
        stepIds.add(milestone.id);
        changed = true;
    }

    if (!changed) {
        return trace;
    }
    return {
        status,
        steps,
        nextTransientStepNumber,
    };
}
